import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CircleSlash, HelpCircle, Minus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  KPI_STATUS_LABELS,
  SEVERITIES,
  type DataQualityState,
  type KpiStatus,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Card,
  CardHeader,
  Callout,
  Drawer,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  StatusPill,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { formatDate, humanise } from '../../lib/format';

/**
 * The executive overview.
 *
 * Its job is to be trustworthy before it is impressive (directive §24). Every
 * card shows its source, its period and an explicit status, and a card with no
 * data says so in words rather than showing a zero. The KPI availability and
 * data quality panels exist for one reason: to stop absence of data being read
 * as good performance.
 */

interface Kpi {
  key: string;
  label: string;
  value: number | null;
  unit: string | null;
  source: string;
  sourceName: string;
  target: string | null;
  status: KpiStatus;
  period: string;
  targetScope: 'PILOT' | 'SELECTION' | 'NONE';
  explanation: string;
  drilldown: string | null;
}

interface DataQualityRow {
  key: string;
  label: string;
  state: DataQualityState;
  captured: number | null;
  backfilled: number | null;
  detail: string;
}

interface MachineRow {
  machineId: string;
  machineCode: string;
  machineName: string;
  status: 'NORMAL' | 'ATTENTION' | 'INSUFFICIENT_DATA';
  reasons: string[];
  openProblems: number | null;
  criticalProblems: number | null;
  ciltCompliance: number | null;
}

interface Overview {
  period: { from: string | null; to: string | null; label: string; spanDays: number | null };
  scoped: boolean;
  kpis: Kpi[];
  cilt: {
    completed: number;
    late: number;
    partial: number;
    missed: number;
    pending: number;
    accountable: number;
    rate: number | null;
  };
  problems: {
    raised: number;
    stillOpen: number;
    closed: number;
    critical: number;
    overdue: number;
    noDueDate: number;
    slaConfigured: boolean;
  };
  closure: { closedInPeriod: number; closedAllTime: number };
  recurrence: {
    groups: number;
    problemsInGroups: number;
    problemsExamined: number;
    rule: { description: string; windowDays: number };
  };
  learning: {
    opls: { total: number; published: number; byType: Record<string, number> };
    training: { completionRate: number | null; competencyRate: number | null; needsRetraining: number };
    certification: { certified: number; target: number; rule: string };
  };
  topProblemAreas: Array<{
    key: string | null;
    label: string;
    sublabel: string | null;
    raised: number;
    stillOpen: number;
    critical: number;
  }>;
  machines: MachineRow[];
  dataQuality: DataQualityRow[];
}

const STATUS_TONE: Record<KpiStatus, 'ok' | 'warn' | 'alarm' | 'neutral' | 'info'> = {
  ACHIEVED: 'ok',
  WITHIN_TARGET: 'ok',
  BELOW_TARGET: 'alarm',
  UNAVAILABLE: 'neutral',
  INSUFFICIENT_DATA: 'neutral',
  NOT_APPLICABLE: 'neutral',
};

const QUALITY_TONE: Record<DataQualityState, 'ok' | 'warn' | 'alarm' | 'neutral'> = {
  LIVE: 'ok',
  BACKFILL: 'warn',
  UNKNOWN: 'warn',
  NOT_CONNECTED: 'neutral',
  NOT_APPLICABLE: 'neutral',
};

const QUALITY_ICON: Record<DataQualityState, typeof CheckCircle2> = {
  LIVE: CheckCircle2,
  BACKFILL: AlertTriangle,
  UNKNOWN: HelpCircle,
  NOT_CONNECTED: CircleSlash,
  NOT_APPLICABLE: Minus,
};

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

export function ManagementOverviewPage() {
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [machineId, setMachineId] = useState('');
  const [lineId, setLineId] = useState('');
  const [severity, setSeverity] = useState('');
  const [explaining, setExplaining] = useState<Kpi | null>(null);

  const filters = {
    from: `${from}T00:00:00.000Z`,
    to: `${to}T23:59:59.999Z`,
    machineId: machineId || undefined,
    lineId: lineId || undefined,
    severity: severity || undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'management', filters],
    queryFn: () => api.get<Overview>(`/api/analytics/management${qs(filters)}`),
  });

  const { data: machines } = useQuery({
    queryKey: ['machines', 'picker'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; code: string; name: string; lineId: string; lineName: string }> }>(
        '/api/machines',
      ),
  });

  const lines = [
    ...new Map((machines?.data ?? []).map((m) => [m.lineId, m.lineName])).entries(),
  ];

  // Cards that carry a target lead; the rest follow.
  const targeted = data?.kpis.filter((k) => k.target !== null) ?? [];
  const plain = data?.kpis.filter((k) => k.target === null) ?? [];

  return (
    <div>
      <PageHeader
        title="Management overview"
        subtitle="Every figure with its source, its period and what it can and cannot tell you."
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="From">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field label="Line">
              <Select
                value={lineId}
                onChange={(e) => {
                  setLineId(e.target.value);
                  setMachineId('');
                }}
              >
                <option value="">All lines</option>
                {lines.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Machine">
              <Select value={machineId} onChange={(e) => setMachineId(e.target.value)}>
                <option value="">All machines</option>
                {(machines?.data ?? [])
                  .filter((m) => !lineId || m.lineId === lineId)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">Any severity</option>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {humanise(s)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>

        {isLoading || !data ? (
          <SkeletonRows rows={5} />
        ) : (
          <>
            {/* §16: the reporting period is never ambiguous. */}
            <Callout tone="info" title={`Reporting period · ${data.period.label}`}>
              {data.period.spanDays === null
                ? 'All time.'
                : `${data.period.spanDays} days.`}{' '}
              {data.scoped
                ? 'This view is filtered, so pilot-wide targets are not judged against it — those are marked "Pilot scope".'
                : 'Unfiltered, covering every line and machine.'}
            </Callout>

            {/* --- Target vs actual ------------------------------------- */}
            <Card>
              <CardHeader
                title="Target versus actual"
                subtitle="Targets are from the pilot proposal, unchanged. Click a row for how the figure was reached."
              />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b border-steel-200 text-left text-xs tracking-wide text-steel-500 uppercase">
                      <th className="px-4 py-2 font-semibold">KPI</th>
                      <th className="px-3 py-2 text-right font-semibold">Actual</th>
                      <th className="px-3 py-2 font-semibold">Target</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                      <th className="px-4 py-2 font-semibold">Period</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-steel-100">
                    {targeted.map((kpi) => (
                      <tr
                        key={kpi.key}
                        className="cursor-pointer hover:bg-steel-50"
                        onClick={() => setExplaining(kpi)}
                      >
                        <td className="px-4 py-2 font-semibold text-steel-900">
                          {kpi.label}
                          {kpi.targetScope === 'PILOT' ? (
                            <span className="ml-2 text-xs font-normal text-steel-500">
                              Pilot scope
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right font-bold tabular-nums">
                          {/* §29: a missing value is a dash with a reason, never 0. */}
                          {kpi.value === null ? (
                            <span className="text-steel-400">—</span>
                          ) : (
                            `${kpi.value}${kpi.unit === '%' ? '%' : ''}`
                          )}
                        </td>
                        <td className="px-3 py-2 text-steel-700">{kpi.target}</td>
                        <td className="px-3 py-2">
                          <Badge tone={STATUS_TONE[kpi.status]}>
                            {KPI_STATUS_LABELS[kpi.status]}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-steel-600">{kpi.sourceName}</td>
                        <td className="px-4 py-2 text-xs text-steel-500">{kpi.period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* --- Untargeted operational figures ----------------------- */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {plain.map((kpi) => (
                <button key={kpi.key} type="button" onClick={() => setExplaining(kpi)}>
                  <Card className="h-full p-4 text-left">
                    <p className="text-xs font-semibold tracking-wide text-steel-500 uppercase">
                      {kpi.label}
                    </p>
                    <p className="mt-2 text-3xl font-bold tabular-nums text-steel-900">
                      {kpi.value === null ? (
                        <span className="text-base font-semibold text-steel-500">
                          {KPI_STATUS_LABELS[kpi.status]}
                        </span>
                      ) : (
                        `${kpi.value}${kpi.unit === '%' ? '%' : ''}`
                      )}
                    </p>
                    <p className="mt-1 text-xs text-steel-500">{kpi.sourceName}</p>
                  </Card>
                </button>
              ))}
            </div>

            {/* --- Machine attention ------------------------------------ */}
            <Card>
              <CardHeader
                title="Machines needing attention"
                subtitle="Status comes from the machine health rules — every ATTENTION names the rules that fired."
              />
              {data.machines.length === 0 ? (
                <EmptyState
                  icon={AlertTriangle}
                  title="No machines in scope"
                  message="No active machine matches the current filter."
                />
              ) : (
                <ul className="divide-y divide-steel-100">
                  {data.machines.map((machine) => (
                    <li key={machine.machineId}>
                      <Link
                        to={`/machines/${machine.machineId}/history`}
                        className="block px-4 py-3 transition hover:bg-steel-50"
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-bold text-steel-900">
                            {machine.machineCode}
                            <span className="ml-2 font-normal text-steel-500">
                              {machine.machineName}
                            </span>
                          </p>
                          <StatusPill
                            tone={
                              machine.status === 'ATTENTION'
                                ? 'warn'
                                : machine.status === 'NORMAL'
                                  ? 'ok'
                                  : 'neutral'
                            }
                            label={
                              machine.status === 'INSUFFICIENT_DATA'
                                ? 'Insufficient data'
                                : humanise(machine.status)
                            }
                          />
                        </div>
                        <p className="mt-0.5 text-xs text-steel-600">
                          {machine.reasons.length > 0
                            ? `This machine ${machine.reasons.join(', and ')}.`
                            : 'No attention rule matched.'}
                        </p>
                        {/*
                          §19: a null count renders as a dash, never as 0.
                          "0 open" and "we do not know" are different claims.
                        */}
                        <p className="mt-0.5 text-xs text-steel-500">
                          {machine.openProblems ?? '—'} open ·{' '}
                          {machine.criticalProblems ?? '—'} critical · CILT{' '}
                          {machine.ciltCompliance === null
                            ? 'not scheduled'
                            : `${machine.ciltCompliance}%`}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* --- CILT breakdown ------------------------------------ */}
              <Card>
                <CardHeader
                  title="CILT compliance"
                  subtitle="The windows behind the rate"
                  action={
                    <Link
                      to="/analytics/cilt"
                      className="text-xs font-bold text-info-700 hover:underline"
                    >
                      Open CILT analytics →
                    </Link>
                  }
                />
                {data.cilt.accountable === 0 ? (
                  <p className="px-4 py-3 text-sm text-steel-600">
                    Nothing was scheduled in this period, so there is no compliance figure. This
                    is not 0%.
                  </p>
                ) : (
                  <dl className="px-4 py-2">
                    <Row label="Scheduled and closed">{data.cilt.accountable}</Row>
                    <Row label="Completed on time">{data.cilt.completed}</Row>
                    <Row label="Late">{data.cilt.late}</Row>
                    <Row label="Partially completed">{data.cilt.partial}</Row>
                    <Row label="Missed">{data.cilt.missed}</Row>
                    <Row label="Not yet due (excluded)">{data.cilt.pending}</Row>
                  </dl>
                )}
              </Card>

              {/* --- Problem signals ---------------------------------- */}
              <Card>
                <CardHeader
                  title="Top problem areas"
                  subtitle="By component, most problems first"
                  action={
                    <Link
                      to="/analytics/problems"
                      className="text-xs font-bold text-info-700 hover:underline"
                    >
                      Open problem intelligence →
                    </Link>
                  }
                />
                {data.topProblemAreas.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-steel-600">
                    No Problem Tag was raised in this period.
                  </p>
                ) : (
                  <ul className="divide-y divide-steel-100">
                    {data.topProblemAreas.slice(0, 6).map((area) => (
                      <li
                        key={area.key ?? area.label}
                        className="flex items-center justify-between gap-3 px-4 py-2"
                      >
                        <span className="min-w-0 text-sm">
                          <span className="font-semibold text-steel-900">{area.label}</span>
                          {area.sublabel ? (
                            <span className="ml-1 text-xs text-steel-500">{area.sublabel}</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-xs tabular-nums text-steel-600">
                          {area.raised} raised · {area.stillOpen} open
                          {area.critical > 0 ? (
                            <span className="ml-1 font-bold text-alarm-600">
                              · {area.critical} critical
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* --- Recurrence --------------------------------------- */}
              <Card>
                <CardHeader title="Recurring issues" subtitle={data.recurrence.rule.description} />
                <dl className="px-4 py-2">
                  <Row label="Recurring groups">{data.recurrence.groups}</Row>
                  <Row label="Tags in a recurring group">
                    {data.recurrence.problemsInGroups}
                  </Row>
                  <Row label={`Tags examined (${data.recurrence.rule.windowDays}-day window)`}>
                    {data.recurrence.problemsExamined}
                  </Row>
                </dl>
                <p className="border-t border-steel-100 px-4 py-2 text-xs text-steel-500">
                  These are three different numbers: groups, tags belonging to one, and tags the
                  rule looked at.
                </p>
              </Card>

              {/* --- Learning ----------------------------------------- */}
              <Card>
                <CardHeader
                  title="Learning"
                  subtitle={data.learning.certification.rule}
                  action={
                    <Link
                      to="/analytics/learning"
                      className="text-xs font-bold text-info-700 hover:underline"
                    >
                      Open learning →
                    </Link>
                  }
                />
                <dl className="px-4 py-2">
                  <Row label="Operators certified">
                    {data.learning.certification.certified} / {data.learning.certification.target}
                  </Row>
                  <Row label="OPLs published">{data.learning.opls.published}</Row>
                  <Row label="Training completion">
                    {data.learning.training.completionRate === null
                      ? 'No training assigned'
                      : `${data.learning.training.completionRate}%`}
                  </Row>
                  <Row label="Assessed competent">
                    {data.learning.training.competencyRate === null
                      ? 'No training assigned'
                      : `${data.learning.training.competencyRate}%`}
                  </Row>
                  <Row label="Needs retraining">
                    {data.learning.training.needsRetraining}
                  </Row>
                </dl>
              </Card>
            </div>

            {/* --- Data quality ---------------------------------------- */}
            <Card>
              <CardHeader
                title="Data quality"
                subtitle="What ODR can and cannot see. An unconnected feed is not good performance."
              />
              <ul className="divide-y divide-steel-100">
                {data.dataQuality.map((row) => {
                  const Icon = QUALITY_ICON[row.state];
                  return (
                    <li key={row.key} className="flex gap-3 px-4 py-3">
                      <Icon
                        aria-hidden
                        className={
                          row.state === 'LIVE'
                            ? 'mt-0.5 size-4 shrink-0 text-ok-600'
                            : row.state === 'NOT_CONNECTED'
                              ? 'mt-0.5 size-4 shrink-0 text-steel-400'
                              : 'mt-0.5 size-4 shrink-0 text-warn-600'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold text-steel-900">{row.label}</p>
                          <Badge tone={QUALITY_TONE[row.state]}>{humanise(row.state)}</Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-steel-600">{row.detail}</p>
                        {/* §20: captured and backfilled are never merged. */}
                        {row.backfilled !== null && row.backfilled > 0 ? (
                          <p className="mt-0.5 text-xs font-semibold text-warn-700">
                            Captured {row.captured} · backfilled {row.backfilled}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </>
        )}
      </div>

      {/* --- KPI explanation ------------------------------------------- */}
      <Drawer
        open={explaining !== null}
        onClose={() => setExplaining(null)}
        title={explaining?.label ?? ''}
        subtitle={explaining ? KPI_STATUS_LABELS[explaining.status] : ''}
      >
        {explaining ? (
          <div className="space-y-4">
            <dl>
              <Row label="Actual">
                {explaining.value === null
                  ? KPI_STATUS_LABELS[explaining.status]
                  : `${explaining.value}${explaining.unit === '%' ? '%' : (explaining.unit ? ` ${explaining.unit}` : '')}`}
              </Row>
              <Row label="Target">{explaining.target ?? 'No target in the proposal'}</Row>
              <Row label="Target scope">
                {explaining.targetScope === 'PILOT'
                  ? 'Pilot programme — does not change with the filter'
                  : explaining.targetScope === 'SELECTION'
                    ? 'Moves with the current filter'
                    : 'No target'}
              </Row>
              <Row label="Source">{explaining.sourceName}</Row>
              <Row label="Provenance">{humanise(explaining.source)}</Row>
              <Row label="Period">{explaining.period}</Row>
            </dl>

            <Callout
              tone={
                explaining.status === 'UNAVAILABLE' || explaining.status === 'INSUFFICIENT_DATA'
                  ? 'info'
                  : 'ok'
              }
              title="How this figure was reached"
            >
              {explaining.explanation}
            </Callout>

            {explaining.drilldown ? (
              <Link
                to={explaining.drilldown}
                className="inline-block text-sm font-bold text-info-700 hover:underline"
              >
                See the records behind it →
              </Link>
            ) : (
              <p className="text-sm text-steel-500">
                There are no records to drill into — ODR has no feed for this measure.
              </p>
            )}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-steel-100 py-2 last:border-0">
      <dt className="text-sm text-steel-600">{label}</dt>
      <dd className="text-right text-sm font-semibold tabular-nums text-steel-900">{children}</dd>
    </div>
  );
}
